import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import { useSession, useSignIn, useSignOut } from '../auth/queries';
import { AnnouncementBanner } from '../mail/AnnouncementBanner';
import type { TicketAccess } from './accessApi';
import { PortalKnowledge } from './PortalKnowledge';
import { PortalTicketDetail } from './PortalTicketDetail';
import { PortalTicketList } from './PortalTicketList';
import { usePortalSettings } from './queries';
import { TicketAccessForm } from './TicketAccessForm';
import { TicketAccessView } from './TicketAccessView';

/**
 * Das Kundenportal -- F4-17 bis F4-23.
 *
 * **Ein eigenes Buendel.** Die Datei wird ueber `lazyRouteComponent` geladen
 * und importiert nichts aus `features/tickets` oder `app/AppLayout`; jeder
 * solche Import zoege die Agentenoberflaeche in das Portalbuendel. Was das
 * Portal von den Tickets braucht, steht in `features/portal/api.ts` --
 * dieselben Endpunkte, eigene Typen.
 *
 * **Kein zweiter Router.** Das Portal hat drei Ansichten: Vorgaenge, ein
 * Vorgang, Wissen. Ein eigener Routenbaum dafuer waere Aufwand fuer eine
 * Adresszeile, die im Portal niemand liest -- und er zoege den Router in ein
 * Buendel, das ohne ihn auskommt.
 *
 * **Die Sichtbarkeitsregeln stehen nicht hier.** Was ein Portalnutzer sieht,
 * entscheidet der Server (F4-19, F4-22, F4-23). Diese Datei zeigt an, was sie
 * bekommt -- ein zweiter Filter im Browser waere kein Schutz, sondern die
 * Stelle, an der jemand spaeter glaubt, der erste sei entbehrlich.
 */
export default function PortalApp() {
  const session = useSession();

  // Der geoeffnete Vorgang steht hier und nicht in der Adresszeile: Das
  // Merkmal oeffnet einen Schriftwechsel und hat weder im Browserverlauf
  // noch in einem Zugriffsprotokoll etwas zu suchen (F4-35). Wer die Seite
  // neu laedt, kommt ueber den Link aus seiner Mail in einem Zug wieder
  // herein -- das ist der Weg, den er ohnehin genommen hat.
  const [access, setAccess] = useState<TicketAccess | null>(null);

  if (access) {
    return (
      <PortalFrame>
        <TicketAccessView
          access={access}
          onChanged={setAccess}
          onClose={() => {
            setAccess(null);
          }}
        />
      </PortalFrame>
    );
  }

  if (session.isPending) return <LoadingIndicator />;
  if (!session.data) return <PortalEntrance onOpened={setAccess} />;

  return <PortalShell />;
}

/**
 * Die Eingangsseite ohne Anmeldung.
 *
 * **Der Vorgangszugang steht oben, die Anmeldung darunter** -- und das ist
 * keine Geschmacksfrage. Die Kunden dieses Systems haben in aller Regel
 * keinen Portalzugang; sie haben eine Mail mit einer Vorgangsnummer. Was die
 * Mehrheit braucht, gehoert an die erste Stelle, und die Anmeldung bleibt
 * fuer die, die ein Konto haben.
 */
function PortalEntrance({ onOpened }: { onOpened: (access: TicketAccess) => void }) {
  // Aus der Adresszeile und nicht aus dem Router: Diese Seite wird ueber
  // einen Link aus einer Mail geoeffnet, also mit einem vollstaendigen
  // Seitenaufruf. Ein Routenabgleich brauchte ein Suchschema an der Route
  // und braechte fuer diesen einen Fall nichts hinzu.
  const [params] = useState(() => new URLSearchParams(window.location.search));

  return (
    <PortalFrame narrow>
      <TicketAccessForm
        initialEmail={params.get('adresse') ?? ''}
        initialTicketNumber={params.get('vorgang') ?? ''}
        onOpened={onOpened}
      />
      <PortalLogin />
    </PortalFrame>
  );
}

/**
 * Der Rahmen fuer alles, was ohne Anmeldung erreichbar ist.
 *
 * Keine Navigation: Es gibt nichts, wohin man von hier aus wechseln koennte.
 *
 * **Der Auftritt des Mandanten bleibt hier blass**, und das ist kein
 * Versehen: `/portal/settings` verlangt eine Anmeldung, weil ohne sie kein
 * Mandant feststeht -- die Adresse `/portal` ist fuer alle dieselbe. Wer
 * nicht angemeldet ist, sieht deshalb "Service-Portal" statt des
 * Kundennamens. Das war vor F4-35 an der Anmeldeseite genauso; die Farben
 * und das Logo (F4-21) greifen, sobald jemand angemeldet ist.
 */
function PortalFrame({ children, narrow }: { children: React.ReactNode; narrow?: boolean }) {
  const { t } = useTranslation();
  const settings = usePortalSettings();

  const brandStyle = settings.data?.primary_color
    ? ({ '--color-primary': settings.data.primary_color } as React.CSSProperties)
    : undefined;

  return (
    <div className="shell" style={brandStyle}>
      <a className="skip-link" href="#portal-main">
        {t('nav.skipToContent')}
      </a>

      <header className="shell__bar">
        <div className="row">
          {settings.data?.logo_url ? (
            <img src={settings.data.logo_url} alt="" style={{ height: '1.5rem' }} />
          ) : null}
          <strong>{settings.data?.tenant_name ?? t('portal.title')}</strong>
        </div>
      </header>

      <main className={narrow ? 'shell__main shell__main--narrow' : 'shell__main'} id="portal-main">
        {children}
      </main>
    </div>
  );
}

/**
 * Die Anmeldung des Portals.
 *
 * Getrennt von der Anmeldung der Agentenoberflaeche, obwohl sie denselben
 * Endpunkt benutzt: Der Text ist ein anderer, und ein Kunde soll auf einer
 * Seite landen, die nach seinem Dienstleister aussieht und nicht nach dessen
 * Werkzeugkasten.
 *
 * Seit F4-35 ist sie eine Karte auf der Eingangsseite und nicht mehr die
 * Eingangsseite selbst: Den Auftritt des Mandanten traegt jetzt
 * ``PortalFrame``, und darunter stehen beide Wege nebeneinander.
 */
function PortalLogin() {
  const { t } = useTranslation();
  const signIn = useSignIn();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  return (
    <section className="card">
      <h2>{t('portal.signInTitle')}</h2>
      <p className="table__muted">{t('portal.loginHint')}</p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          signIn.mutate({ email, password });
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor="portal-email">
            {t('auth.email')}
          </label>
          <input
            id="portal-email"
            type="email"
            autoComplete="username"
            className="field__control"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="portal-password">
            {t('auth.password')}
          </label>
          <input
            id="portal-password"
            type="password"
            autoComplete="current-password"
            className="field__control"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </div>

        {signIn.isError ? <ErrorNotice error={signIn.error} /> : null}

        <Button type="submit" variant="primary" busy={signIn.isPending}>
          {t('auth.signIn')}
        </Button>
      </form>
    </section>
  );
}

type PortalView = 'tickets' | 'knowledge';

function PortalShell() {
  const { t } = useTranslation();
  const settings = usePortalSettings();
  const signOut = useSignOut();

  const [view, setView] = useState<PortalView>('tickets');
  const [ticketId, setTicketId] = useState<string | null>(null);

  // Die Markenfarbe wird als Token gesetzt und nicht als Eigenschaft an
  // einzelne Elemente: So gilt sie fuer alles, was sie benutzt -- und der
  // Dunkelmodus bleibt unangetastet. Der Wert ist serverseitig auf '#rrggbb'
  // geprueft (modules/tenants/branding.py).
  const brandStyle = settings.data?.primary_color
    ? ({ '--color-primary': settings.data.primary_color } as React.CSSProperties)
    : undefined;

  return (
    <div className="shell" style={brandStyle}>
      {/* Derselbe Sprunglink wie in der Agentenoberflaeche. Ohne ihn muss
          sich jemand, der mit der Tastatur arbeitet, auf jeder Seite erneut
          durch die Kopfleiste bewegen, bevor er den Inhalt erreicht -- und
          im Portal sitzen die Anwender, denen niemand eine Einweisung
          gegeben hat. */}
      <a className="skip-link" href="#portal-main">
        {t('nav.skipToContent')}
      </a>

      <header className="shell__bar">
        <div className="row">
          {settings.data?.logo_url ? (
            <img
              src={settings.data.logo_url}
              alt=""
              style={{ height: '1.5rem' }}
              // Leerer Alternativtext: Das Logo steht neben dem Namen des
              // Mandanten und traegt keine eigene Aussage. Ein
              // Alternativtext waere fuer die Sprachausgabe eine
              // Wiederholung.
            />
          ) : null}
          <strong>{settings.data?.tenant_name ?? t('portal.title')}</strong>
        </div>

        <nav className="row" aria-label={t('portal.title')}>
          <Button
            variant={view === 'tickets' ? 'secondary' : 'ghost'}
            aria-pressed={view === 'tickets'}
            onClick={() => {
              setView('tickets');
              setTicketId(null);
            }}
          >
            {t('portal.myTickets')}
          </Button>
          <Button
            variant={view === 'knowledge' ? 'secondary' : 'ghost'}
            aria-pressed={view === 'knowledge'}
            onClick={() => {
              setView('knowledge');
            }}
          >
            {t('portal.knowledge')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              signOut.mutate();
            }}
          >
            {t('auth.signOut')}
          </Button>
        </nav>
      </header>

      <main className="shell__main shell__main--narrow" id="portal-main">
        {/* Stoerungsbanner -- F3-10, K-56. Der Kunde soll die Stoerung sehen,
            bevor er ein Ticket dazu schreibt; das erspart beiden Seiten den
            Vorgang. `portal` waehlt die Ankuendigungen, die fuer ihn
            freigegeben sind -- interne gehen ihn nichts an. */}
        <AnnouncementBanner portal />

        {settings.data?.greeting && view === 'tickets' && ticketId === null ? (
          <div className="notice notice--info" role="status">
            {settings.data.greeting}
          </div>
        ) : null}

        {view === 'knowledge' ? <PortalKnowledge /> : null}

        {view === 'tickets' && ticketId === null ? (
          <PortalTicketList
            onOpen={setTicketId}
            onCreated={() => {
              setTicketId(null);
            }}
          />
        ) : null}

        {view === 'tickets' && ticketId !== null ? (
          <PortalTicketDetail
            ticketId={ticketId}
            onBack={() => {
              setTicketId(null);
            }}
          />
        ) : null}
      </main>
    </div>
  );
}
